import { AppServices, AssetType, OrderType, TrustType, type OrderBuyReq } from 'network'
import { useAssetsStore, useBaseInfoStore, useKlineStore, useSpotOrderListStore } from 'store'
import { useI18n } from 'i18n-utils'
import { UiUtils } from 'ui-components'

export enum ButtonType {
  Market = 'Market',
  Limit = 'Limit',
  StopLoss = 'StopLoss',
}

export const useSpotOrdererBuy = () => {
  /** 交易额 */
  const tradeAmount = ref('')
  const klineStore = useKlineStore()
  const assetsStore = useAssetsStore()
  const buttonType = ref<ButtonType>(ButtonType.Market)
  const slider = ref(0)
  const baseInfoStore = useBaseInfoStore()
  const { t } = useI18n()
  const spotOrderListStore = useSpotOrderListStore()

  /** 买入数量 */
  const buyAmount = ref('')

  /** 买入价格 */
  const buyPrice = ref('')

  /** 是否初始化设置过金额 */
  const isInitPrice = ref(false)

  /** 买入数量是否聚焦 */
  const isBuyAmountFocus = ref(false)

  /** 是否正在买入 */
  const isLoading = ref(false)

  const fromSpotSymbolName = computed(() => {
    return klineStore.getSpotFromSymbolName()
  })

  const toSpotSymbolName = computed(() => {
    return klineStore.getSpotToSymbolName()
  })

  /** 可用资产 */
  const availableAssets = computed(() => {
    if (!klineStore.currentSymbolId) {
      return ''
    }
    const staticData = baseInfoStore.getCoinStaticItemDataBySymbolId(klineStore.currentSymbolId)
    if (!staticData) return assetsStore.availableAssets
    const coinTo = staticData.coinTo
    const data = assetsStore.assets?.remain.list.find((item) => item.itemId === coinTo)

    return data?.over || ''
  })

  const getMarks = (value: number) => {
    if (value === slider.value) {
      return `${value}%`
    }
    return ''
  }

  const sliderMarks = computed(() => {
    // 只有当slider的值等对应的百分比时显示marks，其他都显示空
    const marks = {
      0: getMarks(0),
      25: getMarks(25),
      50: getMarks(50),
      75: getMarks(75),
      100: getMarks(100),
    }
    return marks
  })
  const buttonTypeList = [
    {
      label: '市价',
      value: ButtonType.Market,
    },
    {
      label: '限价',
      value: ButtonType.Limit,
    },
  ]

  watchEffect(() => {
    if (!isInitPrice.value && klineStore.currentSpotKline) {
      isInitPrice.value = true
      buyPrice.value = `${klineStore.currentSpotKline?.close?.decimalPadZero(klineStore.coinToPrecision) || ''}`
    }
  })

  watchEffect(() => {
    if (buttonType.value === ButtonType.Market) {
      buyPrice.value = `${klineStore.currentSpotKline?.close?.decimalPadZero(klineStore.coinToPrecision) || ''}`
    }
  })

  watchEffect(() => {
    const rate = slider.value / 100
    if (!availableAssets.value) {
      tradeAmount.value = '--'
      return
    }
    if (isNaN(rate)) {
      tradeAmount.value = '--'
      return
    }
    const num = availableAssets.value.multiplyStr(rate)
    if (!num) {
      tradeAmount.value = '--'
      return
    }
    tradeAmount.value = num.cutDecimal(2)
  })

  const orderBuy = async (successCb?: () => void) => {
    if (isLoading.value) return
    if (!klineStore.currentSymbolId) {
      return
    }

    if (tradeAmount.value.isGreaterThan(availableAssets.value!) || availableAssets.value?.isEqualTo(0)) {
      UiUtils.showWarning(t('可用余额不足'))
      return
    }

    if (buttonType.value === ButtonType.Limit && !buyPrice.value) {
      UiUtils.showWarning(t('请输入买入价格'))
      return
    }

    isLoading.value = true
    const req: OrderBuyReq = {
      trustType: TrustType.Normal,
      assetType: AssetType.Spot,
      type: buttonType.value === ButtonType.Market ? OrderType.Market : OrderType.Limit,
      symbolId: klineStore.currentSymbolId,
      side: 'buy',
    }
    if (buttonType.value === ButtonType.Limit) {
      req.price = buyPrice.value
      req.num = buyAmount.value
    } else {
      req.total = tradeAmount.value
    }
    const res = await AppServices.spotOrderBuy(req)
    if (res.status === 200) {
      UiUtils.showSuccess(t('下单成功'))
      assetsStore.refresh()
      spotOrderListStore.refresh()
      successCb?.()
    } else {
      UiUtils.showError(res.msg)
    }
    isLoading.value = false
  }

  const onClear = () => {
    buyAmount.value = ''
    slider.value = 0
    tradeAmount.value = '0.00'
  }

  const handleButtonTypeChange = (value: ButtonType) => {
    buttonType.value = value
    isInitPrice.value = true
    onClear()
    switch (value) {
      case ButtonType.Market:
        buyPrice.value = `${klineStore.currentSpotKline?.close || ''}`
        break
      case ButtonType.Limit:
        buyPrice.value = `${klineStore.currentSpotKline?.close || ''}`
        break
      case ButtonType.StopLoss:
        break
      default:
        break
    }
  }

  const onSliderInput = (value: number) => {
    if (isBuyAmountFocus.value) return

    const rate = value.divideStr(100)
    let marketPrice = `${klineStore.currentSpotKline?.close || ''}`
    if (buttonType.value === ButtonType.Limit) {
      marketPrice = buyPrice.value
    }
    if (!marketPrice) return
    if (!availableAssets.value) return
    const price = availableAssets.value?.multiplyStr(rate)
    if (!price) return
    buyAmount.value = price.divideStr(marketPrice)
  }

  const onBuyAmountFocus = () => {
    isBuyAmountFocus.value = true
  }

  const onBuyAmountBlur = () => {
    isBuyAmountFocus.value = false
  }

  watch(
    () => buyAmount.value,
    (val) => {
      if (buttonType.value === ButtonType.Limit && val.isGreaterThanOrEqualTo(availableAssets.value)) {
        buyAmount.value = availableAssets.value
      }
    },
  )

  const onBuyAmountInput = (evt: string | MouseEvent) => {
    let value = ''
    if (typeof evt === 'string') {
      value = evt
    } else {
      value = (evt.target as HTMLInputElement).value
    }
    if (!value) {
      slider.value = 0
      return
    }
    // 通过可用金额，计算出可最大购买的数量
    if (!availableAssets.value) return
    const marketPrice = klineStore.currentSpotKline?.close
    if (!marketPrice) return
    const maxAmount = availableAssets.value.divideStr(marketPrice)
    if (value.isGreaterThan(maxAmount)) {
      slider.value = 100
    } else {
      slider.value = value.divideStr(maxAmount).multiplyStr(100).cutDecimal(2).toNumber()
    }
  }

  return {
    onClear,
    buyPrice,
    buyAmount,
    buttonType,
    buttonTypeList,
    handleButtonTypeChange,
    slider,
    sliderMarks,
    fromSpotSymbolName,
    availableAssets,
    orderBuy,
    tradeAmount,
    isBuyAmountFocus,
    onBuyAmountFocus,
    onBuyAmountBlur,
    onBuyAmountInput,
    toSpotSymbolName,
    isLoading,
    onSliderInput,
  }
}
